import { conciergeService } from '../../../_services/concierge.service';
import { readConversation } from './transcript';
import { tidyVoices } from './voices';

/**
 * The front desk's data, in the shape the pages want. Everything goes through
 * the concierge endpoints the old settings page used; nothing new is needed
 * from the server for this phase (docs/FRONT_DESK_BACKEND.md lists what the
 * next one wants).
 */

// House responseHandler wraps as { statusCode, responseData }. The API answers
// 200 with statusCode 0 when something went wrong, so axios does not reject
// and a plain unwrap would hand the error back as data.
export function unwrap(res) {
    const body = (res && res.data) || {};
    if (body.statusCode === 0) {
        const err = new Error((body.error && body.error.responseMessage) || body.message || 'That did not work');
        err.payload = body;
        throw err;
    }
    return (body.responseData !== undefined ? body.responseData : body.data) || body;
}

export const errorText = (e, fallback) => (e && e.message) || (e && e.response && e.response.data && e.response.data.error && e.response.data.error.responseMessage) || fallback;

export const EMPTY_SETTINGS = Object.freeze({
    enabled: false, assistantName: 'Bailey', greeting: '', openingLine: '', voiceEnabled: false, voiceNumber: '',
    voiceTransferNumber: '', voiceProvider: '', voiceId: ''
});

export function tidySettings(raw) {
    const s = raw || {};
    return {
        enabled: !!s.enabled,
        // It is always Bailey. Whatever the field holds, the page shows Bailey
        // and the next save writes it.
        assistantName: 'Bailey',
        greeting: s.greeting || '',
        // The salon's own first line. Blank: the server opens in the soul's style.
        openingLine: s.openingLine || '',
        // No number, no phone: never "live on the phone" with nothing to ring.
        voiceEnabled: !!s.voiceEnabled && !!s.voiceNumber,
        voiceNumber: s.voiceNumber || '',
        voiceTransferNumber: s.voiceTransferNumber || '',
        voiceProvider: s.voiceProvider || '',
        voiceId: s.voiceId || '',
        // Not sent by the server yet; shown when it is (pence per minute).
        voicePricePerMinute: Number.isFinite(Number(s.voicePricePerMinute)) && s.voicePricePerMinute != null ? Number(s.voicePricePerMinute) : null
    };
}

export async function loadSettings() {
    const d = unwrap(await conciergeService.getSettings());
    return tidySettings(d && d.settings);
}

export async function loadAnswers() {
    const d = unwrap(await conciergeService.getFaqs());
    return (d && d.faqs) || [];
}

/** Saves the whole settings object, as the API expects, with the name pinned. */
export async function saveSettings(settings) {
    const out = { ...EMPTY_SETTINGS, ...settings, assistantName: 'Bailey' };
    delete out.voicePricePerMinute;
    unwrap(await conciergeService.saveSettings(out));
    return tidySettings({ ...settings, assistantName: 'Bailey' });
}

export async function addAnswer(question, answer) {
    unwrap(await conciergeService.saveFaq({ question: String(question || '').trim(), answer: String(answer || '').trim() }));
    return loadAnswers();
}

// Unwrapped like the rest: a 200 that says it failed must not read as removed.
export async function removeAnswer(id) {
    unwrap(await conciergeService.deleteFaq(id));
}

export async function loadMessages() {
    const d = unwrap(await conciergeService.getMessages());
    return ((d && d.messages) || []).map((m) => ({
        id: String(m._id),
        name: (m.clientName || '').trim(),
        contact: (m.clientContact || '').trim(),
        text: (m.message || '').trim(),
        at: m.createdAt || null,
        channel: m.channel || null,
        // The conversation it came from, so it can open it.
        sessionId: m.sessionId ? String(m.sessionId) : null
    }));
}

export async function loadConversations(page) {
    return (await loadConversationPage(page)).list;
}

/**
 * One page of conversations, and how many the page held before the server
 * left some out (`hidden`), so a page of 19 isn't mistaken for the last.
 */
export async function loadConversationPage(page) {
    const d = unwrap(await conciergeService.getTranscripts(page || 0));
    const list = ((d && d.sessions) || []).map(readConversation);
    return { list, size: list.length + (Number(d && d.hidden) || 0) };
}

/* ---------------------------------------------------------------- phone */
export async function findNumbers(areaCode) {
    const d = unwrap(await conciergeService.getNumberOptions({ country: 'GB', areaCode: areaCode || undefined }));
    return (d && d.options) || [];
}

export async function takeNumber(phoneNumber) {
    const d = unwrap(await conciergeService.provisionNumber({ country: 'GB', phoneNumber }));
    return (d && d.number) || phoneNumber;
}

export async function giveNumberBack() {
    unwrap(await conciergeService.releaseNumber());
}

export async function loadVoices() {
    return tidyVoices(unwrap(await conciergeService.getVoices()));
}

// +441614960213 is not how anyone in Britain reads a phone number: 0161 496
// 0213 is. London and the big cities are 3-4-4 or 4-3-4, the rest 5-6.
export function prettyNumber(e164) {
    const d = String(e164 || '').replace(/\s+/g, '');
    const m = /^\+44(\d{9,10})$/.exec(d);
    if (!m) return String(e164 || '');
    const n = `0${m[1]}`;
    if (n.length !== 11) return n;
    if (/^02/.test(n)) return `${n.slice(0, 3)} ${n.slice(3, 7)} ${n.slice(7)}`;
    if (/^01\d1|^011|^0[389]/.test(n)) return `${n.slice(0, 4)} ${n.slice(4, 7)} ${n.slice(7)}`;
    return `${n.slice(0, 5)} ${n.slice(5)}`;
}

export const looksLikePhone = (s) => /^\+?[\d\s()-]{7,}$/.test(String(s || '').trim());

/** A number in international form (+44…), so a call can be put through to it. UK assumed when there's no code. */
export function toE164(value) {
    const raw = String(value || '').trim();
    if (!raw || /[a-z]/i.test(raw)) return '';
    const international = raw.startsWith('+') || raw.startsWith('00');
    let digits = raw.replace(/\D/g, '');
    if (raw.startsWith('00')) digits = digits.slice(2);
    if (international) return digits.length >= 7 && digits.length <= 15 ? `+${digits}` : '';
    if (digits.startsWith('0') && (digits.length === 10 || digits.length === 11)) return `+44${digits.slice(1)}`;
    if (digits.startsWith('44') && (digits.length === 11 || digits.length === 12)) return `+${digits}`;
    return '';
}

/** Empty (any UK number) or a UK area code: 0 then 2 to 4 more digits. */
export const validAreaCode = (v) => { const d = String(v || '').replace(/\s+/g, ''); return !d || /^0\d{2,4}$/.test(d); };

/** What calls cost, as the server says, or plainly that they're charged. */
export function priceLine(settings) {
    const p = settings && settings.voicePricePerMinute;
    return p != null ? `Calls cost ${p}p a minute, only while I’m answering.` : 'Calls are charged per minute, only while I’m answering.';
}
export const looksLikeEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s || '').trim());
export const dialable = (s) => String(s || '').replace(/[^\d+]/g, '');

/* ------------------------------------------------ what's done, for now */
// Messages marked done live in this browser until the server keeps them
// (docs/FRONT_DESK_BACKEND.md). Same pattern as the demo pins.
const salonKey = () => { try { return localStorage.getItem('salonId') || 'me'; } catch (e) { return 'me'; } };
const DONE = () => `beu-frontdesk-done-${salonKey()}`;
export function readDone() {
    try { return JSON.parse(localStorage.getItem(DONE())) || {}; } catch (e) { return {}; }
}
const idsOf = (ids) => (Array.isArray(ids) ? ids : [ids]).filter((x) => x != null).map(String);
/** One message or several, e.g. everything from one person. */
export function markDone(ids) {
    const all = readDone();
    const at = Date.now();
    idsOf(ids).forEach((id) => { all[id] = at; });
    // Keep the newest 2,000 so the list never grows without end, but a
    // "Mark all done" on a long list still holds.
    const keys = Object.keys(all).sort((a, b) => all[b] - all[a]).slice(0, 2000);
    const kept = keys.reduce((o, k) => ({ ...o, [k]: all[k] }), {});
    try { localStorage.setItem(DONE(), JSON.stringify(kept)); } catch (e) { /* this page still remembers */ }
    return kept;
}
/** Back to open, for messages marked done by mistake. */
export function markNotDone(ids) {
    const all = { ...readDone() };
    idsOf(ids).forEach((id) => { delete all[id]; });
    try { localStorage.setItem(DONE(), JSON.stringify(all)); } catch (e) { /* this page still remembers */ }
    return all;
}

/* ----------------------------------------------- the soul, for Bailey */
// The soul is cached here so Bailey's own context (skills/context.js) can
// read it without a request, and so the pages open instantly.
const SOUL = () => `beu-frontdesk-soul-${salonKey()}`;
export function rememberSoulText(text) {
    try { localStorage.setItem(SOUL(), JSON.stringify({ text: String(text || ''), at: Date.now() })); } catch (e) { /* ignore */ }
}
export function recallSoulText() {
    try { const v = JSON.parse(localStorage.getItem(SOUL())); return v && typeof v.text === 'string' ? v.text : null; } catch (e) { return null; }
}

/* ------------------------------------------------- parked switch-on */
const PARK = () => `beu-frontdesk-setup-${salonKey()}`;
export function readParked() {
    try { return JSON.parse(localStorage.getItem(PARK())) || null; } catch (e) { return null; }
}
export function park(state) {
    try {
        if (state) localStorage.setItem(PARK(), JSON.stringify({ ...state, at: Date.now() }));
        else localStorage.removeItem(PARK());
    } catch (e) { /* ignore */ }
}

/** True for a salon that has never put Bailey on the front desk. */
export function neverUsed(settings, answers) {
    const s = settings || {};
    return !s.enabled && !s.voiceEnabled && !s.voiceNumber && !String(s.greeting || '').trim() && !(answers || []).length;
}

/** Today's conversations by channel, from what the server sent. Never guessed. */
export function todayCounts(conversations, now = new Date()) {
    const day = now.toDateString();
    let chats = 0; let calls = 0;
    (conversations || []).forEach((c) => {
        if (!c.at || new Date(c.at).toDateString() !== day) return;
        if (c.channel === 'phone') calls += 1; else chats += 1;
    });
    return { chats, calls };
}

/** "6 chats and 2 calls today", or nothing yet. */
export function todayLine(counts) {
    const bits = [];
    if (counts.chats) bits.push(`${counts.chats} ${counts.chats === 1 ? 'chat' : 'chats'}`);
    if (counts.calls) bits.push(`${counts.calls} ${counts.calls === 1 ? 'call' : 'calls'}`);
    return bits.length ? `${bits.join(' and ')} today` : 'Nothing yet today';
}

export function modesLine(settings) {
    const s = settings || {};
    if (s.enabled && s.voiceEnabled) return 'Chat and phone';
    if (s.voiceEnabled) return 'Phone';
    if (s.enabled) return 'Chat';
    return 'Off';
}
